import { alpha } from '@mui/material/styles';
import { iconButtonClasses } from '@mui/material/IconButton';
import { gray } from '../../getDesignTokens';

/**
 * The tabular-data pair — MuiTablePagination and MuiDataGrid live in
 * one file because DataGrid uses TablePagination in its footer and
 * both are always styled to look like a coordinated pair.
 *
 * MuiDataGrid is the biggest single override in the theme:
 *   - Card-like shell (rounded corners, elevated shadow)
 *   - Uppercase column headers with a subtle branded tint background
 *   - Zebra rows via nth-of-type(2n)
 *   - Vertical-center of cell content (MUI's default aligns to top,
 *     which reads as broken on avatar / two-line rows)
 *   - Overlay-scrollbar theming that ONLY appears in the data area,
 *     not over the column headers
 *   - Column-separator (resize handle) that stays invisible until the
 *     user hovers the header, matching the dividerless aesthetic
 *
 * MUI's defaults are calibrated for a spreadsheet look; these overrides
 * turn every DataGrid into a card-like list view without callers
 * needing to reproduce all of the above per page.
 */
export const dataGridCustomizations = {
  MuiTablePagination: {
    styleOverrides: {
      actions: {
        display: 'flex',
        gap: 8,
        marginRight: 6,
        [`& .${iconButtonClasses.root}`]: {
          minWidth: 0,
          width: 36,
          height: 36,
        },
      },
    },
  },
  MuiDataGrid: {
    defaultProps: {
      density: 'comfortable',
      rowHeight: 52,
      columnHeaderHeight: 48,
    },
    styleOverrides: {
      root: ({ theme }) => {
        const isDark = theme.palette.mode === 'dark';
        const borderColor = alpha(theme.palette.divider, isDark ? 0.6 : 0.3);
        const hoverColor = alpha(theme.palette.primary.main, isDark ? 0.16 : 0.08);
        const headerBg = alpha(theme.palette.primary.main, isDark ? 0.22 : 0.06);
        const zebraColor = alpha(theme.palette.primary.main, isDark ? 0.18 : 0.04);

        return {
          border: `1px solid ${borderColor}`,
          borderRadius: theme.spacing(2),
          backgroundColor: theme.palette.background.paper,
          boxShadow: isDark
            ? '0 12px 40px rgba(0,0,0,0.45)'
            : '0 24px 45px rgba(15, 23, 42, 0.08)',
          overflow: 'hidden',
          '--DataGrid-cellBorderWidth': '0',
          '--DataGrid-rowBorderColor': borderColor,
          '--DataGrid-containerBackground': theme.palette.background.paper,

          '& .MuiDataGrid-overlay': {
            backgroundColor: alpha(theme.palette.background.default, isDark ? 0.7 : 0.85),
            backdropFilter: 'blur(6px)',
          },

          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: headerBg,
            backdropFilter: 'blur(4px)',
            borderBottom: `1px solid ${borderColor}`,
            overflow: 'hidden',
            // Hide any native scrollbar that appears in the header area
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          },

          '& .MuiDataGrid-columnHeader': {
            paddingInline: theme.spacing(2),
            fontWeight: 600,
            letterSpacing: 0.3,
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            '&:focus, &:focus-within': {
              outline: 'none',
            },
            '&.MuiDataGrid-columnHeader--sorted': {
              color: theme.palette.text.primary,
            },
          },

          // Column separator = resize handle. Keeping it in the DOM (so
          // users can grab it and resize) but invisible by default to
          // preserve the clean, dividerless look. Reveals on column-header
          // hover, and stays visible (tinted brand blue) while an active
          // drag is in progress.
          '& .MuiDataGrid-columnSeparator': {
            opacity: 0,
            transition: 'opacity 160ms ease',
            color: borderColor,
          },
          '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
            opacity: 1,
          },
          '& .MuiDataGrid-columnSeparator--resizing': {
            opacity: 1,
            color: theme.palette.primary.main,
          },

          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${alpha(borderColor, 0.5)}`,
            color: theme.palette.text.primary,
            fontSize: theme.typography.body2.fontSize,
            paddingInline: theme.spacing(2),
            transition: 'background-color 160ms ease, color 160ms ease',
            // Vertically center all cell content. MUI DataGrid's default
            // aligns to the top, which reads as broken on taller rows
            // (avatar cells, cells with two-line content, cells sharing a
            // row with a stacked control like a toggle + label). Pages
            // used to override this per-DataGrid; centralised here so
            // every list page inherits the correct alignment.
            display: 'flex',
            alignItems: 'center',
            '&:focus, &:focus-within': {
              outline: 'none',
            },
            '&.MuiDataGrid-cell--editing': {
              backgroundColor: alpha(theme.palette.warning.main, 0.14),
              boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.warning.main, 0.4)}`,
            },
          },

          '& .MuiDataGrid-row': {
            transition: 'transform 160ms ease, background-color 160ms ease',
            '&:hover': {
              backgroundColor: hoverColor,
              transform: 'translate3d(0, -1px, 0)',
            },
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.28 : 0.12),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.32 : 0.18),
              },
            },
            '&:nth-of-type(2n)': {
              backgroundColor: zebraColor,
            },
          },

          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${borderColor}`,
            backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.9 : 0.95),
            backdropFilter: 'blur(4px)',
            padding: theme.spacing(0, 2),
            '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
              fontWeight: 500,
              letterSpacing: 0.2,
            },
          },

          '& .MuiDataGrid-toolbarContainer': {
            padding: theme.spacing(1.5, 2),
            gap: theme.spacing(1),
            borderBottom: `1px solid ${borderColor}`,
            backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.85 : 0.92),
            '& .MuiButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: theme.shape.borderRadius,
            },
          },

          // ─── Scrollbar theming ──────────────────────────────────────────
          // Hide the native scrollbar on virtualScroller; MUI DataGrid renders
          // its own overlay scrollbar elements (.MuiDataGrid-scrollbar--vertical /
          // --horizontal) that we style below.
          '& .MuiDataGrid-virtualScroller': {
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          },

          // MUI overlay vertical scrollbar:
          // By default it starts at top:0 (covering the column headers).
          // Push it down by the header height so it only appears in the data area.
          '& .MuiDataGrid-scrollbar--vertical': {
            top: 'var(--DataGrid-headersTotalHeight)',
            height: 'calc(100% - var(--DataGrid-headersTotalHeight))',
            width: 6,
            // Firefox
            scrollbarWidth: 'thin',
            scrollbarColor: `${isDark ? gray[600] : gray[400]} transparent`,
            // Webkit
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? gray[700] : gray[300],
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: isDark ? gray[500] : gray[400],
            },
            '&::-webkit-scrollbar-corner': { background: 'transparent' },
          },

          // MUI overlay horizontal scrollbar
          '& .MuiDataGrid-scrollbar--horizontal': {
            height: 6,
            scrollbarWidth: 'thin',
            scrollbarColor: `${isDark ? gray[600] : gray[400]} transparent`,
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? gray[700] : gray[300],
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: isDark ? gray[500] : gray[400],
            },
          },
        };
      },
    },
  },
};
